import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import type { ISiteOption } from "../../../customTypings/customer/Customer";
import type { ICustomerSelectItem } from "../../../customTypings/location/Location";
import { locationsService } from "../../../services/locations/locations.service";
import { ReactSelectBootstrapStyle } from "../../../styles/ReactSelectBootstrapStyle";
import { getUserFacingErrorMessage, logErrorForDev, showToastError, showToastSuccess } from "../../../utils";

export interface AddCustomerAssociationModalProps {
    show: boolean;
    locationId: string;
    onClose: () => void;
    onAssociated: () => void;
}

const NO_LINKABLE_CUSTOMERS_MESSAGE =
    "There are no customers available to link to this location. Eligible customers may already be linked, or none exist at your accessible sites.";

const AddCustomerAssociationModal = ({
    show,
    locationId,
    onClose,
    onAssociated,
}: AddCustomerAssociationModalProps) => {
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [siteId, setSiteId] = useState("");
    const [customers, setCustomers] = useState<ICustomerSelectItem[]>([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
    const [label, setLabel] = useState("");
    const [isMainLocation, setIsMainLocation] = useState(false);
    const [associating, setAssociating] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const showSitePicker = sites.length > 1;
    const hasLinkableCustomers = sites.length > 0;
    const noLinkableOptions = !hasLinkableCustomers || sitesLoading;
    const formDisabled = noLinkableOptions || associating;

    useEffect(() => {
        if (!show) {
            setSites([]);
            setSiteId("");
            setSelectedCustomerId(null);
            setLabel("");
            setIsMainLocation(false);
            setLoadError(null);
            return;
        }

        setSitesLoading(true);
        locationsService
            .listLinkableSitesForCustomerAssociation(locationId)
            .then((list) => {
                setSites(list);
                setSiteId(list.length === 1 ? list[0].id : "");
                setLoadError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("AddCustomerAssociationModal.sites", e);
                setSites([]);
                setSiteId("");
                setLoadError(getUserFacingErrorMessage(e, "Could not load sites."));
            })
            .finally(() => setSitesLoading(false));
    }, [show, locationId]);

    useEffect(() => {
        if (!show || !siteId) {
            setCustomers([]);
            setSelectedCustomerId(null);
            return;
        }
        setCustomersLoading(true);
        locationsService
            .listAvailableCustomersForAssociation(locationId, siteId)
            .then((rows) => {
                setCustomers(rows);
                setSelectedCustomerId(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("AddCustomerAssociationModal.customers", e);
                setCustomers([]);
                setSelectedCustomerId(null);
                setLoadError(getUserFacingErrorMessage(e, "Could not load customers."));
            })
            .finally(() => setCustomersLoading(false));
    }, [show, siteId, locationId]);

    const customerOptions = useMemo(
        () => customers.map((c) => ({ value: c.id, label: c.name })),
        [customers]
    );

    const selectedCustomerOption = useMemo(
        () => customerOptions.find((o) => o.value === selectedCustomerId) ?? null,
        [customerOptions, selectedCustomerId]
    );

    const customerPlaceholder = useMemo(() => {
        if (sitesLoading) return "Loading…";
        if (!hasLinkableCustomers) return "No customers available";
        if (!siteId) return "Choose a site first";
        if (customersLoading) return "Loading…";
        if (customerOptions.length === 0) return "No customers available";
        return "Select a customer…";
    }, [sitesLoading, hasLinkableCustomers, siteId, customersLoading, customerOptions.length]);

    const handleAssociate = () => {
        if (!selectedCustomerId) return;
        setAssociating(true);
        locationsService
            .associateCustomer(locationId, {
                customerId: selectedCustomerId,
                label: label.trim() || null,
                isMainLocation,
            })
            .then(() => {
                showToastSuccess("Customer linked.");
                onAssociated();
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not link customer."));
            })
            .finally(() => setAssociating(false));
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Link a customer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadError && <div className="text-danger mb-3">{loadError}</div>}
                {!sitesLoading && !loadError && !hasLinkableCustomers && (
                    <Alert variant="info" className="mb-3">
                        {NO_LINKABLE_CUSTOMERS_MESSAGE}
                    </Alert>
                )}
                <div className="row g-3 mb-3">
                    {showSitePicker && (
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label>Site</Form.Label>
                                <Form.Select
                                    value={siteId}
                                    onChange={(e) => {
                                        setSiteId(e.target.value);
                                        setSelectedCustomerId(null);
                                    }}
                                    disabled={noLinkableOptions}
                                    aria-label="Site"
                                >
                                    <option value="">Select a site…</option>
                                    {sites.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.siteName}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    )}
                    <div className={showSitePicker ? "col-md-8" : "col-12"}>
                        <Form.Group>
                            <Form.Label>Customer</Form.Label>
                            <Select
                                inputId="add-customer-association-customer"
                                options={customerOptions}
                                value={selectedCustomerOption}
                                onChange={(option) => setSelectedCustomerId(option?.value ?? null)}
                                isDisabled={noLinkableOptions || !siteId || customersLoading}
                                isLoading={customersLoading || sitesLoading}
                                isClearable
                                placeholder={customerPlaceholder}
                                styles={ReactSelectBootstrapStyle}
                                classNamePrefix="react-select"
                            />
                        </Form.Group>
                    </div>
                </div>
                <div className="mb-3">
                    <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Optional"
                            disabled={formDisabled}
                        />
                    </Form.Group>
                </div>
                <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                    <Form.Check
                        type="checkbox"
                        id="add-customer-association-main"
                        label="Set as primary address"
                        checked={isMainLocation}
                        onChange={(e) => setIsMainLocation(e.target.checked)}
                        disabled={!selectedCustomerId || formDisabled}
                    />
                    <Button
                        variant="primary"
                        onClick={handleAssociate}
                        disabled={!selectedCustomerId || formDisabled}
                    >
                        {associating ? "Linking…" : "Link customer"}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export { AddCustomerAssociationModal };
